/**
 * Reset Password Form Validation Schema
 * Zod schema for validating password reset form data
 */

import { z } from 'zod';

/**
 * Password requirements definition
 * Used for both validation and displaying requirements to users
 */
export const passwordRequirements = [
  {
    key: 'length',
    label: 'At least 8 characters',
    test: (password: string) => password.length >= 8,
  },
  {
    key: 'uppercase',
    label: 'One uppercase letter',
    test: (password: string) => /[A-Z]/.test(password),
  },
  {
    key: 'lowercase',
    label: 'One lowercase letter',
    test: (password: string) => /[a-z]/.test(password),
  },
  {
    key: 'number',
    label: 'One number',
    test: (password: string) => /[0-9]/.test(password),
  },
] as const;

/**
 * Reset password form validation schema
 * Validates password strength and confirmation match
 */
export const resetPasswordSchema = z
  .object({
    password: z
      .string()
      .min(1, 'Password is required')
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Z]/, 'Password must contain an uppercase letter')
      .regex(/[a-z]/, 'Password must contain a lowercase letter')
      .regex(/[0-9]/, 'Password must contain a number'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

/**
 * TypeScript type inferred from schema
 */
export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

/**
 * Default values for the reset password form
 */
export const resetPasswordFormDefaults: ResetPasswordFormData = {
  password: '',
  confirmPassword: '',
};
